import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { UserPlus, MessageCircle, Mail, Phone, Copy, Send, Award } from "lucide-react";
import { whatsappLink } from "@/lib/whatsapp";

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS = [
  { key: "contacted", label: "Contactei", color: "#3B82F6" },
  { key: "interested", label: "Interessado", color: "#22C55E" },
  { key: "declined", label: "Não quer", color: "#737373" },
];

export default function PublicRecruit() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const load = () => axios.get(`${API}/api/public/recruit/${token}`).then((r) => setD(r.data))
    .catch((e) => setError(e.response?.data?.detail || "Link inválido."));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <Shell><p className="text-[#FF6B6B]">{error}</p></Shell>;
  if (!d) return <Shell><p className="kw-overline">A carregar…</p></Shell>;
  const open = d.status === "open";
  return (
    <Shell>
      <Toaster theme="dark" />
      <div className="kw-overline mb-2 flex items-center gap-2"><UserPlus className="w-4 h-4 text-[#22C55E]" /> Recrutamento</div>
      <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter">
        Reforços para os <span className="text-[#E50000]">{d.club}</span>
      </h1>
      <p className="text-sm text-[#A3A3A3] mt-3 max-w-2xl">
        Olá {d.me}! {open ? `Há ${d.vacancies - d.filled} vaga(s). Contacta colegas do clube, marca o estado de cada contacto e vê o que os outros já fizeram.` : "Este pedido já fechou. Obrigado pela ajuda!"}
        {" "}Quem recrutar um colega que entre no plantel ganha +20 pontos.
      </p>
      {d.note && <p className="mt-3 text-sm border-l-4 border-[#E50000] pl-3">{d.note}</p>}
      <div className="grid md:grid-cols-2 gap-3 mt-6">
        {d.candidates.map((c) => <Candidate key={c.id} c={c} token={token} open={open} onChange={load} />)}
      </div>
    </Shell>
  );
}

function Candidate({ c, token, open, onChange }) {
  const mine = c.contacts.find((x) => x.mine)?.status;
  const others = c.contacts.filter((x) => !x.mine);
  const wa = whatsappLink(c.phone);
  const setStatus = async (status) => {
    try { await axios.post(`${API}/api/public/recruit/${token}/status`, { candidate_id: c.id, status }); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const invite = async () => {
    if (!window.confirm(`Enviar a ${c.name} um convite por email com link para aceitar?`)) return;
    try { await axios.post(`${API}/api/public/recruit/${token}/invite`, { candidate_id: c.id }); toast.success("Convite enviado"); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  return (
    <div className="kw-card p-4 space-y-2" data-testid={`recruit-candidate-${c.id}`}>
      <div className="flex gap-3">
        {c.photo_url ? <img src={`${API}${c.photo_url}`} alt="" className="w-12 h-12 object-cover" />
          : <div className="w-12 h-12 bg-[#1a1a1a] flex items-center justify-center font-black">{(c.name || "?")[0]}</div>}
        <div className="flex-1 min-w-0">
          <div className="font-bold truncate">{c.name}</div>
          <div className="text-[10px] uppercase tracking-widest text-[#737373] flex items-center gap-2">
            {c.mbti}{c.years ? <span className="inline-flex items-center gap-0.5 text-[#EAB308]"><Award className="w-3 h-3" />{c.years} {c.years === 1 ? "ano" : "anos"}</span> : null}
          </div>
          {c.phone && <a href={`tel:${c.phone}`} className="text-[11px] text-[#A3A3A3] flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</a>}
          {c.email && <a href={`mailto:${c.email}?subject=${encodeURIComponent("Convite Growth League")}&body=${encodeURIComponent(c.invite_text)}`}
            className="text-[11px] text-[#A3A3A3] flex items-center gap-1 truncate"><Mail className="w-3 h-3" />{c.email}</a>}
        </div>
      </div>
      {others.length > 0 && (
        <div className="text-[11px] text-[#A3A3A3]" data-testid="recruit-others">
          {others.map((o, i) => <div key={i}>{o.recruiter_name}: <b style={{ color: STATUS.find((s) => s.key === o.status)?.color }}>{STATUS.find((s) => s.key === o.status)?.label}</b></div>)}
        </div>
      )}
      {open && (
        <>
          <div className="flex flex-wrap gap-1.5">
            {wa && <a href={`${wa}?text=${encodeURIComponent(c.invite_text)}`} target="_blank" rel="noreferrer"
              className="px-2 py-1 border border-[#25D366]/60 text-[#25D366] text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1"
              data-testid={`recruit-wa-${c.id}`}><MessageCircle className="w-3 h-3" /> WhatsApp</a>}
            <button onClick={() => { navigator.clipboard.writeText(c.invite_text); toast.success("Mensagem copiada"); }}
              className="px-2 py-1 border border-[#333] text-[#A3A3A3] text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1"><Copy className="w-3 h-3" /> Copiar convite</button>
            {c.email && <button onClick={invite} className="px-2 py-1 border border-[#E50000]/60 text-[#E50000] text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1"
              data-testid={`recruit-invite-${c.id}`}><Send className="w-3 h-3" /> Convite com link</button>}
          </div>
          <div className="flex gap-1.5">
            {STATUS.map((s) => (
              <button key={s.key} onClick={() => setStatus(s.key)}
                className="px-2 py-1 border text-[10px] uppercase tracking-widest font-bold transition-colors"
                style={mine === s.key ? { borderColor: s.color, color: "#fff", background: s.color } : { borderColor: "#333", color: "#737373" }}
                data-testid={`recruit-status-${s.key}-${c.id}`}>{s.label}</button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#0A0A0A] text-white px-5 py-10">
    <div className="max-w-4xl mx-auto kw-fade-up">{children}</div>
  </div>
);
